import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import { useTenant } from "../../hooks/useTenant";
import { resetPasswordRequest } from "../../api/api";
import CustomAppBar from "../customAppBar";
import LoadingScreen from "../../shared_modules/loadingScreen";
import myVideo from "../../images/bellosvideo.mp4";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { tenant, status } = useTenant();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });

  if (status === "loading") return <LoadingScreen />;

  if (!token) {
    return (
      <>
        <CustomAppBar tenant={tenant} showLoginButton={false} />
        <Box
          sx={{
            mt: "64px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 64px)",
            px: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 450 }}>
            Invalid or missing reset link. Please request a new one from the{" "}
            <span
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => navigate("/forgot")}
            >
              forgot password
            </span>{" "}
            page.
          </Alert>
        </Box>
      </>
    );
  }

  const handleReset = async () => {
    setAlert({ show: false, message: "", type: "" });

    if (password.length < 8) {
      setAlert({ show: true, message: "Password must be at least 8 characters.", type: "error" });
      return;
    }
    if (password !== confirmPassword) {
      setAlert({ show: true, message: "Passwords do not match.", type: "error" });
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPasswordRequest(token, password, confirmPassword);
      navigate("/login?message=password_reset");
    } catch (err) {
      setAlert({ show: true, message: err.message || "Could not reset password. The link may have expired.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomAppBar tenant={tenant} showLoginButton={false} />

      <Grid
        container
        sx={{ height: "calc(100vh - 64px)", minHeight: "500px", mt: "64px" }}
      >
        <Grid size={{ xs: 12, lg: 7 }}>
          <Box
            sx={{
              backgroundColor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", lg: "center" },
              flexDirection: "column",
              height: "100%",
              padding: { xs: "30px 20px", lg: "30px 50px" },
            }}
          >
            <Typography variant="h3">Reset Password</Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 1, mb: 4, fontWeight: 300 }}
            >
              Enter your new password below
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "customDivider",
                padding: "30px",
                borderRadius: "24px",
                width: { xs: "100%", sm: "450px" },
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              }}
            >
              {alert.show && (
                <Alert severity={alert.type} sx={{ mb: 2 }}>
                  {alert.message}
                </Alert>
              )}

              <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 300, pb: 1 }}>
                New Password
              </Typography>
              <TextField
                type="password"
                variant="outlined"
                placeholder="Enter new password"
                value={password}
                disabled={isSubmitting}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 300, pb: 1, mt: 2 }}>
                Confirm Password
              </Typography>
              <TextField
                type="password"
                variant="outlined"
                placeholder="Confirm new password"
                value={confirmPassword}
                disabled={isSubmitting}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleReset()}
              />

              <Button
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                sx={{ mt: 5 }}
                onClick={handleReset}
              >
                {isSubmitting ? (
                  <CircularProgress size="30px" sx={{ color: "text.primary" }} />
                ) : (
                  "Set New Password"
                )}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 300 }}>
                  Back to
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => navigate("/login")}
                  sx={{
                    color: "text.primary",
                    fontWeight: 300,
                    ml: 1,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline", textUnderlineOffset: "5px" },
                  }}
                >
                  Login
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid size={5} sx={{ display: { xs: "none", lg: "flex" }, padding: "15px" }}>
          <video
            width="100%"
            height="100%"
            controls={false}
            muted
            autoPlay
            loop
            playsInline
            preload="auto"
            style={{ objectFit: "cover", borderRadius: "15px" }}
          >
            <source src={myVideo} type="video/mp4" />
          </video>
        </Grid>
      </Grid>
    </>
  );
};

export default ResetPassword;
